import { RequireAuth } from "@fusionauth/react-sdk";
import { Button, Grid } from "@mui/material";
import React, { useContext, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import BasePage from "../../components/layout/BasePage.tsx";
import Header from "../../components/layout/Header.tsx";
import ProjectLockedBanner from "../../components/project/ProjectLockedBanner.tsx";
import {
  ProjectContext,
  ProjectDispatchContext,
} from "../../contexts/ProjectContext";
import { convertProjectToProjectContext } from "../../models/Project.ts";
import { useFetch } from "../../services/hooks/useFetch";
import { projectsPrefix } from "../../services/ProjectsServices";
import AdditionalDataPage from "./SettingPages/AdditionalDataPage";
import CustomCategories from "./SettingPages/categories/CustomCategoriesPage";
import CompanySettingsPage from "./SettingPages/CompanySettingsPage";
import DeviceDetailsSettings from "./SettingPages/DeviceDetailsSettings";
import FlowsheetGroupsPage from "./SettingPages/flowsheet/FlowsheetGroupsPage";
import IncrementalSettingsPage from "./SettingPages/IncrementalSettingsPage.tsx";
import LabGroupsPage from "./SettingPages/lab/LabGroupsPage";
import ManufacturerSettingsPage from "./SettingPages/ManufacturerSettingsPage";
import PatientCohortPage from "./SettingPages/PatientCohortPage.tsx";
import PrioritizationPage from "./SettingPages/PrioritizationPage";
import ProjectDetailsSettingsPage from "./SettingPages/ProjectDetailsSettingsPage";
import ProjectPermissionsPage from "./SettingPages/ProjectPermissionsPage";
import PromGroupsPage from "./SettingPages/prom/PromGroupsPage";
import ProtocolPage from "./SettingPages/ProtocolPage";
import ProjectSnapshotsPage from "./SettingPages/snapshot/ProjectSnapshotsPage";
import UserSettingsPage from "./SettingPages/user/UserSettingsPage";
import SettingsBreadcrumb from "./SettingsBreadcrumb";

export default function BaseSettingsPage() {
  const [searchParams] = useSearchParams();
  const currentSetting = searchParams.get("settings") || "";
  const currentProject = searchParams.get("projectId");
  const navigate = useNavigate();

  const project = useContext(ProjectContext);
  const dispatch = useContext(ProjectDispatchContext);
  const { fetch: fetchProject } = useFetch<undefined, undefined, Project>();

  useEffect(() => {
    if (!dispatch) return;
    // if we're on a project setting page and the project context is null, fetch it
    if (currentProject && (project === null || !project.id)) {
      void fetchProject(`${projectsPrefix}/${currentProject}`, {
        responseManipulationFunction: (data) => {
          convertProjectToProjectContext(data, dispatch);
          return;
        },
      });
    }
  }, [currentProject, project, dispatch, fetchProject]);

  const getCurrentSettingPage = function (setting: string) {
    // note: this handles waiting for the projectContext if it should be set
    if (currentProject && (project === null || !project.id)) {
      return <></>;
    }

    switch (setting) {
      case "flowsheet-groups":
        return (
          <RequireAuth withRole={"ADMIN"}>
            <FlowsheetGroupsPage />
          </RequireAuth>
        );

      case "lab-groups":
        return (
          <RequireAuth withRole={"ADMIN"}>
            <LabGroupsPage />
          </RequireAuth>
        );

      case "prom-groups":
        return (
          <RequireAuth withRole={"ADMIN"}>
            <PromGroupsPage />
          </RequireAuth>
        );

      case "user":
        return (
          <RequireAuth withRole={"ADMIN"}>
            <UserSettingsPage />
          </RequireAuth>
        );

      case "company":
        return (
          <RequireAuth withRole={"ADMIN"}>
            <CompanySettingsPage />
          </RequireAuth>
        );

      case "device-details":
        return <DeviceDetailsSettings />;

      case "protocol":
        return <ProtocolPage />;

      case "patient-cohort":
        return (
          <RequireAuth withRole={"ADMIN"}>
            <PatientCohortPage />
          </RequireAuth>
        );

      case "project-details":
        return <ProjectDetailsSettingsPage />;

      case "prioritization":
        return <PrioritizationPage />;

      case "custom-categories":
        return (
          <RequireAuth withRole={"ADMIN"}>
            <CustomCategories />
          </RequireAuth>
        );

      case "additional-data":
        return (
          <RequireAuth withRole={"ADMIN"}>
            <AdditionalDataPage />
          </RequireAuth>
        );

      case "manufacturer-settings":
        return (
          <RequireAuth withRole={"ADMIN"}>
            <ManufacturerSettingsPage />
          </RequireAuth>
        );

      case "project-permissions":
        return (
          <RequireAuth withRole={"ADMIN"}>
            <ProjectPermissionsPage />
          </RequireAuth>
        );

      case "snapshot":
        return (
          <RequireAuth withRole={"ADMIN"}>
            <ProjectSnapshotsPage />
          </RequireAuth>
        );

      case "incremental":
        return <IncrementalSettingsPage />;

      default:
        return <></>;
    }
  };

  const getCurrentSettingPageBreadcrumbInfo = function (setting: string) {
    switch (setting) {
      case "flowsheet-groups":
        return { isProject: false, settingsPage: "Flowsheet Groups" };
      case "lab-groups":
        return { isProject: false, settingsPage: "Lab Groups" };
      case "prom-groups":
        return { isProject: false, settingsPage: "Prom Groups" };
      case "user":
        return { isProject: false, settingsPage: "Manage Users" };
      case "company":
        return { isProject: false, settingsPage: "Company Settings" };
      case "manufacturer-settings":
        return { isProject: false, settingsPage: "Manufacturer Settings" };
      case "device-details":
        return { settingsPage: "Device Details" };
      case "protocol":
        return { settingsPage: "Protocol" };
      case "patient-cohort":
        return { settingsPage: "Patient Cohort" };
      case "project-details":
        return { settingsPage: "Project Details" };
      case "prioritization":
        return { settingsPage: "Prioritization" };
      case "additional-data":
        return { settingsPage: "Additional Data" };
      case "project-permissions":
        return { settingsPage: "Project Permissions" };
      case "snapshot":
        return { settingsPage: "Project Snapshots" };
      case "custom-categories":
        return { settingsPage: "Custom Categories" };
      case "incremental":
        return { settingsPage: "Incremental Settings" };
      default:
        return {
          isUnknown: true,
          isProject: false,
          settingsPage: "Unknown",
        };
    }
  };

  const breadcrumbInfo = getCurrentSettingPageBreadcrumbInfo(currentSetting);
  const headerText = breadcrumbInfo.isUnknown
    ? "Oops! That settings page doesn't exist."
    : breadcrumbInfo.isProject
      ? "Project Settings"
      : breadcrumbInfo.settingsPage;

  return (
    <BasePage className="settings-content-box">
      {project && project.isLocked && <ProjectLockedBanner />}
      <Header text={headerText} backgroundColor={"#092b3c"}>
        {breadcrumbInfo.isUnknown && (
          <div className={"header-form-box"}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => navigate("/")}
            >
              Back To Home
            </Button>
          </div>
        )}
      </Header>
      {!breadcrumbInfo.isUnknown && breadcrumbInfo.settingsPage && (
        <Grid container direction="column" className="settings-container">
          <SettingsBreadcrumb {...breadcrumbInfo} />
          {getCurrentSettingPage(currentSetting)}
        </Grid>
      )}
    </BasePage>
  );
}
